// useSatelliteImagery.js — DTG-driven satellite imagery swap + throttled DTG persistence
// (ticket 06 extraction). Composed in MapEditorModalInner right after useOlMapLifecycle,
// consuming the mapRef/mapReady/baseTileLayerRef seam it exposes.

import { useEffect, useRef } from 'react';
import { createBasemapSwapQueue } from '../basemapSwapQueue';
import gibsImageryProvider from '../imagery/gibsImageryProvider';
import { applyImageryCoverage } from '../imagery/applyImageryCoverage';
import { viewBboxLonLat } from './mapGeometryUtils';
import { OVERLAY_TYPES } from '../../schema/mapSchema';

export default function useSatelliteImagery({ mapRef, mapReady, baseTileLayerRef, dtgMs, overlayType, onWindowSettingsChange }) {
    // ponytail (ticket 02): a *separate* queue instance reusing the same generic
    // serialize+stale-check pattern as useOlMapLifecycle's internal basemap-swap queue.
    // Imagery swaps are triggered on every DTG-cursor tick / viewport moveend — much higher
    // frequency than basemap-style changes — and are logically an unrelated "kind" of request
    // (they never touch the mapbox-layers basemap tiles). Funneling both kinds through one
    // queue would make rapid DTG scrubbing serialize behind (and stale-check against) unrelated
    // basemap-style swaps and vice versa, even though the two never race against each other in
    // practice. `createBasemapSwapQueue()` is fully generic (keyed per `map`, arbitrary request
    // id, injected `applyStyle`) so reusing the factory — just a second instance — gets the
    // exact same race-safety guarantees without any changes to basemapSwapQueue.js itself.
    const imagerySwapQueueRef = useRef(null);
    if (!imagerySwapQueueRef.current) imagerySwapQueueRef.current = createBasemapSwapQueue();

    // -- Ticket 02: DTG-cursor-driven satellite imagery layer -----------------
    // On every DTG cursor tick and viewport move, re-evaluate coverage for the *current*
    // viewport bbox + DTG (no zoom-level gating — purely `hasCoverage`-driven per the ticket)
    // and show/hide the `baseTileLayerRef` GIBS TileLayer accordingly, falling back to the
    // default vector basemap (already rendered underneath, untouched) when there's no
    // coverage. Routed through `imagerySwapQueueRef` so rapid scrubbing never lets a stale swap
    // apply after a newer one.
    //
    // Ticket 01: `overlayType` is included in the effect's deps (alongside `dtgMs`) so toggling
    // the overlay selector re-runs this immediately — applyImageryCoverage itself does the
    // actual gating (hides the layer outright when overlayType isn't 'satellite'), this effect
    // just needs to re-invoke it right away rather than waiting for the next DTG tick or moveend.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) return;

        const swap = () => {
            const bbox = viewBboxLonLat(map);
            // basemapId here is repurposed as this queue's generic "request identity" key (see
            // basemapSwapQueue.js) — the DTG value driving this particular swap, not a
            // basemap style id. Using the same dtg value for both the dtg-change-triggered and
            // moveend-triggered calls at that dtg means neither is treated as "stale" relative
            // to the other — both still run, serialized — while a genuinely newer DTG tick
            // does correctly supersede/skip any still-queued older-DTG swap.
            imagerySwapQueueRef.current.request({
                map, basemapId: dtgMs,
                isMapCurrent: () => mapRef.current === map,
                applyStyle: async () => applyImageryCoverage(map, baseTileLayerRef, bbox, dtgMs, gibsImageryProvider, overlayType),
                onError: (e) => console.warn('[MapEditorModal] imagery swap failed', e),
            });
        };

        swap();
        map.on('moveend', swap);
        return () => map.un('moveend', swap);
    }, [mapRef, mapReady, baseTileLayerRef, dtgMs, overlayType]);

    // -- Ticket 01: persist the DTG cursor while Satellite overlay is active --------------------
    // So a later ticket (02, out of scope here) can reproduce the same satellite view in a
    // read-only embed. Throttled (~300ms, same setTimeout/clearTimeout debounce-on-trailing-edge
    // pattern as the coord-grid/viewport-bbox moveend throttles) so rapid DTG scrubbing doesn't
    // spam Yjs writes. Deliberately never writes while overlayType is 'mapbox' — no point
    // persisting a DTG that isn't driving anything visible, and it'd be an unnecessary write
    // on every scrub even when satellite isn't selected.
    // shared-map-data ticket 03: this now writes onto the originating mapNode's own
    // `currentDtgMs` attr (via `onWindowSettingsChange`, MapNodeView._onWindowSettingsChange)
    // rather than the shared map doc's `map_meta` — the satellite-imagery cursor is a per-embed
    // "window" setting like basemapStyleId/overlayType, not shared content.
    const dtgPersistThrottleRef = useRef(null);
    useEffect(() => {
        if (overlayType !== OVERLAY_TYPES.SATELLITE) return;
        clearTimeout(dtgPersistThrottleRef.current);
        dtgPersistThrottleRef.current = setTimeout(() => {
            onWindowSettingsChange?.({ currentDtgMs: dtgMs });
        }, 300);
        return () => clearTimeout(dtgPersistThrottleRef.current);
    }, [overlayType, dtgMs, onWindowSettingsChange]);
}
